"use client";

import { Home } from "@mui/icons-material";
import styles from "./chat-item.module.css";
import Image from "next/image";
import ListingCard from "./listing-card";
import { useState } from "react";
import { MessageType, StoredChat } from "../../../../../lib/chat-store/types";
import { useTranslations } from "next-intl";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";

export default function ChatItem({
  chatData,
  isActiveChat,
  onOpenChatAction: onOpenChat,
}: {
  chatData: StoredChat;
  isActiveChat: boolean;
  onOpenChatAction: () => void;
}) {
  const t = useTranslations("ChatsView");
  const { userData } = useUserAuthDataContext();
  const lastMsg = chatData.last_message;
  const isMe = lastMsg.sender_id === userData.sub;
  const isAssistant = lastMsg.sender_id.startsWith("assistant-");
  let assistantProfile = null;
  if (isAssistant) {
    const assistantSub = lastMsg.sender_id.replace("assistant-", "");
    assistantProfile = chatData.assistants?.[assistantSub];
  }
  let senderName = chatData.receiver_first_name;
  if (isMe) {
    senderName = t("you");
  } else if (isAssistant) {
    senderName = assistantProfile?.name || "AI Assistant";
  }
  let previewText = lastMsg.preview;
  if (isAssistant) {
    if (previewText.startsWith("assistant.")) {
      const personality = assistantProfile?.personality || "professional";
      previewText = t(`${previewText}.${personality}`);
    }
  } else if (lastMsg.type === MessageType.LISTING) {
    previewText = t("shared-property");
  } else if (lastMsg.type === MessageType.MEDIA) {
    previewText = t("sent-file");
  }
  return (
    <button
      className={`${styles.chatWrapper} ${isActiveChat && styles.isActive}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpenChat();
      }}
    >
      <div className={styles.mainContent}>
        <Image
          className={styles.image}
          src={chatData.receiver_photo_url || "/broken_image.webp"}
          width={64}
          height={64}
          alt={`Profile photo of ${chatData.receiver_complete_name}`}
        />
        <div className={styles.contentWrapper}>
          <h3 className={styles.username}>{chatData.receiver_complete_name}</h3>

          <div className={styles.messageWrapper}>
            <span className={styles.sender}>{senderName}:</span>
            <span className={styles.lastMessage}>{previewText}</span>
          </div>
        </div>
      </div>
      <div className={styles.detailsWrapper}>
        <span className={styles.time}>
          {formatMessageTime(chatData.last_updated_at)}
        </span>
        {chatData.unread_count > 0 && (
          <div className={styles.dotWrapper}>
            <span className={styles.unreadMessages}>
              {chatData.unread_count}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}

export function formatMessageTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();

  // strip times to midnight
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date >= today) {
    // today -> show HH:mm
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (date >= yesterday) {
    // yesterday
    return "Yesterday";
  } else {
    // older -> show dd/MM
    return date.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
  }
}

export function ChatItemLoading() {
  return (
    <button
      className={`${styles.chatWrapper} ${styles.isLoadingChats}`}
      disabled
    >
      <div className={styles.mainContent}>
        <div className={`${styles.image} ${styles.isLoading}`} />

        <div className={styles.contentWrapper}>
          <div className={`${styles.username} ${styles.isLoading}`}></div>
          <div className={`${styles.messageWrapper} ${styles.isLoading}`}></div>
        </div>
      </div>

      <div className={styles.detailsWrapper}>
        <span className={`${styles.time} ${styles.isLoading}`} />
        <div className={`${styles.dotWrapper} ${styles.isLoading}`} />
      </div>
    </button>
  );
}
